import React from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import Home from "./routes/home";
import Main from "./routes/main";
import Platform from "./routes/platform";
import ForgetPassword from "./routes/forgetPassword";
import Signup from "./routes/signup";
import Login from "./routes/login";
import Activate from "./app/activate";
import RecoverAccount from "./routes/recoverAccount";
import ErrorPage from "./routes/ErrorPage";
import { NotificationProvider } from "./component/NotificationProvider";
import ErrorBoundary from "./component/ErrorBoundary";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import theme from "./theme";

// Employee
import LeaveRequest from "./app/main/controller/employee-main/leave/index";
import Expense from "./app/main/controller/employee-main/expenses/index";
import AddNewExpense from "./app/main/controller/employee-main/expenses/AddNewExpense";
import MyExpenses from "./app/main/controller/employee-main/expenses/MyExpenses";
import EmployeeAttendanceDashboard from "./app/main/controller/employee-main/attendance/index";
import CompleteProfile from "./app/main/controller/employee-main/onboarding/CompleteProfile";

// Admin
import Employees from './app/main/controller/admin-main/employee/AllEmployees/index';
import AllEmployee from "./app/main/controller/admin-main/employee/AllEmployees/EmployeesTable";
import EmployeeDetails from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/index";
import PersonalDetails from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/PersonalDetails";
import SelfIdentification from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/SelfIdentification";
import ProfileOverview from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileOverview";
import ProfileAttendance from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileAttendance";
import ProfileLeave from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileLeave";
import ProfileExpenses from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileExpenses";
import ProfileTimesheets from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileTimesheets";
import ProfileWorkHistory from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileWorkHistory";
import ProfileCareer from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileCareer";
import ProfileOnboarding from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileOnboarding";
import ProfileGoals from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileGoals";
import Performance from "./app/main/controller/admin-main/performance";
import CycleDetail from "./app/main/controller/admin-main/performance/CycleDetail";
import ReviewQuestions from "./app/main/controller/admin-main/performance/ReviewQuestionsScreen";
import ReviewDetail from "./app/main/controller/admin-main/performance/ReviewDetail";
import EmployeeOnboarding from "./app/main/controller/admin-main/employee/Onboarding/index";
import OnboardingCohort from "./app/main/controller/admin-main/employee/Onboarding/Cohort";
import OnboardingTemplates from "./app/main/controller/admin-main/settings/onboarding/index";
import JoinRequests from "./app/main/controller/admin-main/employee/JoinRequests/index";
import DocumentVerification from "./app/main/controller/admin-main/employee/Documents/index";
import OnboardingInvite from "./app/main/controller/admin-main/employee/Onboarding/OnboardingInvite";
import Dashboard from "./app/main/controller/admin-main/dashboard/index";
import EmployeeDashboard from "./app/main/controller/employee-main/dashboard/index";
import MyOnboarding from "./app/main/controller/employee-main/onboarding/MyOnboarding";
import { isAdminUser } from "./services/role";

import Trips from "./app/main/controller/admin-main/expenses/trips/tripspolicy/index";
import AttendanceMuster from "./app/main/controller/admin-main/attendance/muster";
import AttendanceDashboard from "./app/main/controller/admin-main/attendance/dashboard/index";
import EmployeeAttendance from "./app/main/controller/admin-main/attendance/dashboard/employees-attendance/EmployeeAttendance";
import MyAttendance from "./app/main/controller/admin-main/attendance/my-attendance/index";
import LeaveDashboard from "./app/main/controller/admin-main/leave/dashboard/index";
import RequestDetails from "./app/main/controller/admin-main/leave/dashboard/requests/RequestDetails";
import LeavePolicy from "./app/main/controller/admin-main/leave/leavepolicy/index";
import LeavePolicyListing from "./app/main/controller/admin-main/leave/leavepolicy/PolicyListing";
import LeavePolicyDetails from "./app/main/controller/admin-main/leave/leavepolicy/PolicyDetails";
import LeavePolicyAssignPolicy from "./app/main/controller/admin-main/leave/leavepolicy/assign-policy/index";
import LeavePolicyRules from "./app/main/controller/admin-main/leave/leavepolicy/PolicyRules";
import LeavePolicyReview from "./app/main/controller/admin-main/leave/leavepolicy/ReviewStep";
import TeamLeaveCalendar from "./app/main/controller/admin-main/leave/dashboard/TeamLeaveCalendar";
import LeaveType from "./app/main/controller/admin-main/leave/leavetype/index";
import LeaveTypeListing from "./app/main/controller/admin-main/leave/leavetype/LeaveTypeListing";
import LeaveTypeForm from "./app/main/controller/admin-main/leave/leavetype/LeaveTypeForm";
import Settings from "./app/main/controller/admin-main/settings/index";
import HolidaySettings from "./app/main/controller/admin-main/settings/holidaysettings/index";
import Policy from "./app/main/controller/admin-main/expenses/policy";
import Timesheet from "./app/main/controller/admin-main/timesheet/index";
import TimesheetForm from "./app/main/controller/admin-main/timesheet/TimesheetForm";
import TimesheetDetail from "./app/main/controller/admin-main/timesheet/TimesheetDetail";
import ProjectReport from "./app/main/controller/admin-main/timesheet/ProjectReport";
import TimeSheetGeneralSettings from "./app/main/controller/admin-main/timesheet/general-settings/index";
import Timelog from "./app/main/controller/admin-main/timesheet/general-settings/Timelog";
import EmployeeRate from "./app/main/controller/admin-main/timesheet/general-settings/EmployeeRate";
import ProjectConfiguration from "./app/main/controller/admin-main/timesheet/general-settings/ProjectConfiguration";
import Client from "./app/main/controller/admin-main/timesheet/general-settings/Client";

import ExpensePolicy from "./app/main/controller/admin-main/expenses/policy/index";
import ExpensePolicyListing from "./app/main/controller/admin-main/expenses/policy/ExpensePolicyListing";
import NewExpensePolicy from "./app/main/controller/admin-main/expenses/policy/new/index";
import ExpensePolicyDetails from "./app/main/controller/admin-main/expenses/policy/new/PolicyDetails";
import ExpensePolicyAddCategory from "./app/main/controller/admin-main/expenses/policy/new/AddCategory";
import ExpensePolicyAssignPolicy from "./app/main/controller/admin-main/expenses/policy/new/assign-policy/index";
import ExpensePolicyRules from "./app/main/controller/admin-main/expenses/policy/new/PolicyRules";
import ExpensePolicyReview from "./app/main/controller/admin-main/expenses/policy/new/ReviewStep";
import ExpensePolicyEdit from "./app/main/controller/admin-main/expenses/policy/ExpensePolicyEdit";
import ExpenseDashboard from "./app/main/controller/admin-main/expenses/dashboard/index";
import ExpenseType from "./app/main/controller/admin-main/expenses/policyCategory/index";
import ExpenseTypeListing from "./app/main/controller/admin-main/expenses/policyCategory/ExpenseTypeListing";
import ExpenseTypeForm from "./app/main/controller/admin-main/expenses/policyCategory/ExpenseTypeForm";
import TripsPolicyListing from "./app/main/controller/admin-main/expenses/trips/tripspolicy/TripsPolicyListing";
import NewTripPolicy from "./app/main/controller/admin-main/expenses/trips/tripspolicy/newtrippolicy/index";
import TripsPolicyDetails from "./app/main/controller/admin-main/expenses/trips/tripspolicy/newtrippolicy/PolicyDetails";
import AddVehicleType from "./app/main/controller/admin-main/expenses/trips/tripspolicy/newtrippolicy/AddVehicleType";
import TripsPolicyAssignPolicy from "./app/main/controller/admin-main/expenses/trips/tripspolicy/newtrippolicy/assign-policy/index";
import PolicyRules from "./app/main/controller/admin-main/expenses/trips/tripspolicy/newtrippolicy/PolicyRules";
import TripsPolicyEdit from "./app/main/controller/admin-main/expenses/trips/tripspolicy/TripsPolicyEdit";
import VehicleType from "./app/main/controller/admin-main/expenses/trips/vehicletype/index";
import VehicleListing from "./app/main/controller/admin-main/expenses/trips/vehicletype/VehicleListing";
import VehicleForm from "./app/main/controller/admin-main/expenses/trips/vehicletype/VehicleForm";
import AllTrips from "./app/main/controller/admin-main/expenses/trips/tripsdashboard/AllTrips";
import AssetOverview from "./app/main/controller/admin-main/asset-tracker/overview/index";
import Machines from "./app/main/controller/admin-main/asset-tracker/machine/index";
import MachineListing from "./app/main/controller/admin-main/asset-tracker/machine/MachineListing";
import MachineForm from "./app/main/controller/admin-main/asset-tracker/machine/MachineForm";
import Software from "./app/main/controller/admin-main/asset-tracker/software/index";
import SoftwareListing from "./app/main/controller/admin-main/asset-tracker/software/SoftwareListing";
import SoftwareForm from "./app/main/controller/admin-main/asset-tracker/software/SoftwareForm";
import EditDetails from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/EditDetails";
import Organization from "./app/main/controller/admin-main/organization/index";
import LegalEntities from "./app/main/controller/admin-main/organization/LegalEntities";
import Departments from "./app/main/controller/admin-main/organization/Departments";
import Designations from "./app/main/controller/admin-main/organization/Designations";
import Roles from "./app/main/controller/admin-main/organization/Roles";
import OrgChart from "./app/main/controller/admin-main/organization/OrgChart";

import AllEmployeesAttendance from "./app/main/controller/admin-main/attendance/dashboard/employees-attendance/AllEmployeesAttendance";
import Requests from "./app/main/controller/admin-main/attendance/dashboard/employees-attendance/requests/index";
import RequestsListing from "./app/main/controller/admin-main/attendance/dashboard/employees-attendance/requests/RequestsListing";
import RequestsDetails from "./app/main/controller/admin-main/attendance/dashboard/employees-attendance/requests/RequestsDetails";
import Geofence from "./app/main/controller/admin-main/settings/geofence";
import GradesBands from "./app/main/controller/admin-main/settings/GradesBands";
import AttendancePolicy from "./app/main/controller/admin-main/attendance/policy/index";
import AttendancePolicyListing from "./app/main/controller/admin-main/attendance/policy/AttendancePolicyListing";
import NewAttendancePolicy from "./app/main/controller/admin-main/attendance/policy/new";
import AttendancePolicyDetails from "./app/main/controller/admin-main/attendance/policy/new/PolicyDetails";
import AttendancePolicyAddCategory from "./app/main/controller/admin-main/attendance/policy/new/add-category/index";
import AttendancePolicyRules from "./app/main/controller/admin-main/attendance/policy/new/PolicyRules";
import AttendancePolicyAssignPolicy from "./app/main/controller/admin-main/attendance/policy/new/assign-policy";
import AttendancePolicyReview from "./app/main/controller/admin-main/attendance/policy/new/ReviewStep";
import LeavePolicyEdit from "./app/main/controller/admin-main/leave/leavepolicy/LeavePolicyEdit";
import ShiftListing from "./app/main/controller/admin-main/attendance/shift/creation/ShiftListing";
import ShiftCreation from "./app/main/controller/admin-main/attendance/shift/creation";
import NewShift from "./app/main/controller/admin-main/attendance/shift/creation/new";
import ShiftDetails from "./app/main/controller/admin-main/attendance/shift/creation/new/ShiftDetails";
import AssignShift from "./app/main/controller/admin-main/attendance/shift/creation/new/assign-shift";
import ShiftRules from "./app/main/controller/admin-main/attendance/shift/creation/new/ShiftRules";
import ShiftRotation from "./app/main/controller/admin-main/attendance/shift/rotation";
import ShiftRotationListing from "./app/main/controller/admin-main/attendance/shift/rotation/ShiftRotationListing";
import NewShiftRotation from "./app/main/controller/admin-main/attendance/shift/rotation/new";
import RotationDetails from "./app/main/controller/admin-main/attendance/shift/rotation/new/RotationDetails";
import RotationFrequency from "./app/main/controller/admin-main/attendance/shift/rotation/new/RotationFrequency";
import AssignRotation from "./app/main/controller/admin-main/attendance/shift/rotation/new/assign-rotation";
import EditRotation from "./app/main/controller/admin-main/attendance/shift/rotation/EditRotation";
import EditShift from "./app/main/controller/admin-main/attendance/shift/creation/EditShift";
import AttendancePolicyEdit from "./app/main/controller/admin-main/attendance/policy/AttendancePolicyEdit";
import PayrollLayout from "./app/main/controller/admin-main/payroll-new/PayrollLayout";
import PayrollOverview from "./app/main/controller/admin-main/payroll-new/PayrollDashboard";
import StatutoryReport from "./app/main/controller/admin-main/payroll-new/reports/StatutoryReport";
import SalaryTemplates from "./app/main/controller/admin-main/payroll-new/catalog/SalaryTemplates";
import PayrollRunDashboard from "./app/main/controller/admin-main/payroll-new/run/PayrollRunDashboard";
import PayrollRunDetail from "./app/main/controller/admin-main/payroll-new/run/PayrollRunDetail";
import OffCycleRun from "./app/main/controller/admin-main/payroll-new/run/OffCycleRun";
import SalaryStructureList from "./app/main/controller/admin-main/payroll-new/structure/SalaryStructureList";
import SalaryStructureForm from "./app/main/controller/admin-main/payroll-new/structure/SalaryStructureForm";
import SalaryComponents from "./app/main/controller/admin-main/payroll-new/catalog/SalaryComponents";
import SalaryComponentForm from "./app/main/controller/admin-main/payroll-new/catalog/SalaryComponentForm";
import StatutoryComponents from "./app/main/controller/admin-main/payroll-new/catalog/StatutoryComponents";
import AllPayslips from "./app/main/controller/admin-main/payroll-new/payslip/AllPayslips";
import PayslipQueries from "./app/main/controller/admin-main/payroll-new/payslip/PayslipQueries";
import EncashmentQueue from "./app/main/controller/admin-main/payroll-new/catalog/EncashmentQueue";
import MyTimesheets from "./app/main/controller/employee-main/timesheets/index";
import PayslipDocument from "./app/main/controller/employee-main/payslips/PayslipDocument";
import PayslipDetail from "./app/main/controller/admin-main/payroll-new/payslip/PayslipDetail";
import EmployeePayslips from "./app/main/controller/employee-main/payslips/index";
import TeamApprovals from "./app/main/controller/employee-main/team/index";
import Vendors from "./app/main/controller/admin-main/asset-tracker/vendors/index";
import MyGoals from "./app/main/controller/employee-main/goals/index";
import ProfilePayslips from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfilePayslips";
import ProfileCompensation from "./app/main/controller/admin-main/employee/AllEmployees/employee-details/tabs/ProfileCompensation";
import ExpenseRequests from "./app/main/controller/admin-main/expenses/dashboard/ExpenseRequests";
import ExpenseRequestDetails from "./app/main/controller/admin-main/expenses/dashboard/ExpenseRequestDetails";
import LiveTracking from "./app/main/controller/admin-main/expenses/liveTracking/index";


// /app is home for everyone, and what home means depends on who you are. An
// employee used to land on the admin dashboard's route and see a page built
// from org-wide numbers they have no business reading and no way to act on.
const HomeDashboard = () => (isAdminUser() ? <Dashboard /> : <EmployeeDashboard />);

const appRouter = createBrowserRouter([
  {
    path: "/",
    element: <Home />,
    errorElement: <ErrorPage/>,
  },
  {
    // PayLoom's own console, deliberately outside /app: it runs on a
    // platform-scoped session that the tenant routes refuse, so it cannot sit
    // inside the shell that mints a tenant one.
    path: "/platform",
    element: <Platform />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/app",
    element: <Main />,
    children: [
      // admin

      {
        index: true,
        element: <HomeDashboard />,
      },
      {
        path:"live-tracking",
        element: <LiveTracking/>
      },
      //Trips
      {
        path:"trips",
        element:<Trips/>,
        children:[
          {
             path:"listing",
             element:<TripsPolicyListing/>
          },
          {
            path:"edit",
            element:<TripsPolicyEdit/>
          },
          {
            path:"new",
            element:<NewTripPolicy/>,
            children:[
              {
                path:"setting",
                element:<TripsPolicyDetails/>
              },
              {
                path:"add-vehicle-type",
                element:<AddVehicleType/>
              },
              {
                path:"assign-policy",
                element:<TripsPolicyAssignPolicy/>
              },
              {
                path:"rules",
                element:<PolicyRules/>
              }
            ]
          },
        ]
      },
      {
        path:"vehicle-type",
        element:<VehicleType/>,
        children:[
          {
            index:true,
            element:<VehicleListing/>
          },
          {
            path:"form",
            element:<VehicleForm/>
          }
        ]
      },
      {
        path:"trip-details",
        element:<AllTrips/>
      },
      {
        path:"expense-dashboard",
        element:<ExpenseDashboard/>
      },
      {
        path:"expense-requests",
        element:<ExpenseRequests/>
      },
      {
        path:"expense-requests-details/:id",
        element:<ExpenseRequestDetails/>
      },
      {
        path:"expense-type",
        element:<ExpenseType/>,
        children:[
          {
            index:true,
            element:<ExpenseTypeListing/>
          },
          {
            path:"form",
            element:<ExpenseTypeForm/>
          }
        ]
      },
      {
        path:"expense-policy",
        element:<ExpensePolicy/>,
        children:[
          {
             path:"listing",
             element:<ExpensePolicyListing/>
          },
          {
            path:"edit",
            element:<ExpensePolicyEdit/>
          },
          {
            path:"new",
            element:<NewExpensePolicy/>,
            children:[
              {
                path:"setting",
                element:<ExpensePolicyDetails/>
              },
              {
                path:"add-category",
                element:<ExpensePolicyAddCategory/>
              },
              {
                path:"assign-policy",
                element:<ExpensePolicyAssignPolicy/>
              },
              {
                path:"rules",
                element:<ExpensePolicyRules/>
              },
              {
                path:"review",
                element:<ExpensePolicyReview/>
              }
            ]
          },
        ]
      },
      {
        path: "policy",
        element: <Policy />,
      },
      {
        path: "attendance-dashboard",
        element: <AttendanceDashboard />,
      },
      {
        path: "attendance-muster",
        element: <AttendanceMuster />,
      },
      {
        path: "attendance-details/all-employees",
        element: <AllEmployeesAttendance/>,
      },
      {
        path: "attendance-details/requests",
        element: <Requests/>,
        children:[
          {
            index:true,
            element: <RequestsListing/>,
          },
          {
            path: "details/:id",
            element: <RequestsDetails/>,
          }
        ]
      },
      {
        path: "attendance-details/:employeeId",
        element: <EmployeeAttendance/>,
      },
      {
        path: "attendance-details/my-attendance",
        element: <MyAttendance/>,
      },
      {
        path:"attendance-policy",
        element:<AttendancePolicy/>,
        children:[
          {
             path:"listing",
             element:<AttendancePolicyListing/>
          },
          {
            path:"edit",
            element:<AttendancePolicyEdit/>
          },
          {
            path:"new",
            element:<NewAttendancePolicy/>,
            children:[
              {
                path:"setting",
                element:<AttendancePolicyDetails/>
              },
              {
                path:"add-category",
                element:<AttendancePolicyAddCategory/>
              },
              {
                path:"assign-policy",
                element:<AttendancePolicyAssignPolicy/>
              },
              {
                path:"rules",
                element:<AttendancePolicyRules/>
              },
              {
                path:"review",
                element:<AttendancePolicyReview/>
              }
            ]
          },
        ]
      },
       //shift
       {
        path:"shift",
        element:<ShiftCreation/>,
        children:[
          {
             path:"listing",
             element:<ShiftListing/>
          },
          {
            path:"edit",
            element:<EditShift/>
          },
          {
            path:"new",
            element:<NewShift/>,
            children:[
              {
                path:"details",
                element:<ShiftDetails/>
              },
              {
                path:"assign-shift",
                element:<AssignShift/>
              },
              {
                path:"rules",
                element:<ShiftRules/>
              }
            ]
          },
        ]
      },
      {
        path:"shift-rotation",
        element:<ShiftRotation/>,
        children:[
          {
             path:"listing",
             element:<ShiftRotationListing/>
          },
          {
            path:"edit",
            element:<EditRotation/>
          },
          {
            path:"new",
            element:<NewShiftRotation/>,
            children:[
              {
                path:"rotation-details",
                element:<RotationDetails/>
              },
              {
                path:"rotation-frequency",
                element:<RotationFrequency/>
              },
              {
                path:"assign-rotation",
                element:<AssignRotation/>
              },
            ]
          },
        ]
      },
      //People
      {
        path: "employees",
        element: <Employees/>,
        children:[
          {
            index:true,
            element: <AllEmployee />,
          },
          /*
            The cohort is the screen; adding a joiner is an action on it.
            "onboarding" used to be the add-a-joiner form, which meant the only
            way to see who was stuck was to open records one at a time.
          */
          {
            path: "onboarding",
            element: <OnboardingCohort/>,
          },
          /*
            The same screen for the other end of the lifecycle. One component
            with a phase, because "who is mid-flight and what is outstanding"
            is the same question whether somebody is arriving or leaving, and
            the data behind it is one collection.
          */
          {
            path: "offboarding",
            element: <OnboardingCohort phase="offboarding"/>,
          },
          /*
            People who asked to be added rather than being invited. The signup
            form tells somebody their company is already here; this is where
            that request lands.
          */
          {
            path: "join-requests",
            element: <JoinRequests/>,
          },
          /*
            HR verifies every document an employee hands in; this is the queue.
            A document notification links here.
          */
          {
            path: "documents",
            element: <DocumentVerification/>,
          },
          {
            path: "onboarding/new",
            element: <EmployeeOnboarding/>,
          },
          /*
            The checklist template editor. It has existed as a 354-line screen
            since checklists were built and was routed nowhere — reachable only
            by editing the URL to a path that did not exist. The cohort screen
            offers it, so it needs somewhere to land.
          */
          {
            path: "onboarding/template",
            element: <OnboardingTemplates/>,
          },
          {
            path: "offboarding/template",
            element: <OnboardingTemplates phase="offboarding"/>,
          },
          {
            path: "onboarding-invite",
            element: <OnboardingInvite/>,
          },
        ]
      },
      // Organisation
      {
        path: "organization",
        element: <Organization/>,
        children:[
          { index: true, element: <LegalEntities/> },
          { path: "departments", element: <Departments/> },
          { path: "designations", element: <Designations/> },
          { path: "roles", element: <Roles/> },
          { path: "org-chart", element: <OrgChart/> },
        ]
      },
      {
          path:"employee-details/:employeeId",
          element:<EmployeeDetails/>,
          children:[
            {
              index: true,
              element:<ProfileOverview/>
            },
            {
              path:"profile",
              element:<PersonalDetails/>
            },
            {
              path:"onboarding",
              element:<ProfileOnboarding/>
            },
            {
              path:"attendance",
              element:<ProfileAttendance/>
            },
            {
              path:"leave",
              element:<ProfileLeave/>
            },
            {
              path:"expenses",
              element:<ProfileExpenses/>
            },
            {
              path:"timesheets",
              element:<ProfileTimesheets/>
            },
            {
              path:"compensation",
              element:<ProfileCompensation/>
            },
            {
              path:"payslips",
              element:<ProfilePayslips/>
            },
            {
              path:"career",
              element:<ProfileCareer/>
            },
            {
              path:"goals",
              element:<ProfileGoals/>
            },
            {
              path:"work-history",
              element:<ProfileWorkHistory/>
            },
            {
              path:"self-identification",
              element:<SelfIdentification/>
            }
          ]
      },
      {
        path:"edit-employee-details/:employeeId",
        element:<EditDetails/>
      },
      //Leave
      {
        path: "leave-dashboard",
        element: <LeaveDashboard />,
      },
      {
        path: "leave-calendar",
        element: <TeamLeaveCalendar/>,
      },
      {
        path: "leave-requests-details/:id",
        element: <RequestDetails/>,
      },
      {
        path: "leave-policy",
        element: <LeavePolicy />,
        children:[{
          index:true,
          element:<LeavePolicyListing/>
        },
        {
          path:"settings",
          element:<LeavePolicyDetails/>
        },
        {
          path:"assign-policy",
          element:<LeavePolicyAssignPolicy/>
        },
        {
          path:"rules",
          element:<LeavePolicyRules/>
        },
        {
          path:"review",
          element:<LeavePolicyReview/>
        },
        {
          path:"edit",
          element:<LeavePolicyEdit/>
        },
      ]
      },
      {
        path: "leave-encashment",
        element: <EncashmentQueue />,
      },
      {
        path: "leave-type",
        element: <LeaveType />,
        children:[{
          index:true,
          element:<LeaveTypeListing/>
        },
        {
          path:"form",
          element:<LeaveTypeForm/>
        }
      ]
      },
      {
        path: "assets",
        element: <AssetOverview />,
      },
      {
        path: "machines",
        element: <Machines />,
        children:[{
          index:true,
          element:<MachineListing/>
        },
        {
          path:"form",
          element:<MachineForm/>
        }
      ]
      },
      {
        path: "software",
        element: <Software/>,
        children:[{
          index:true,
          element:<SoftwareListing/>
        },
        {
          path:"form",
          element:<SoftwareForm/>
        }
      ]
      },
      /*
        Vendors is its own module, not a page of Software.
        It was declared inside the `software` children array, which made its
        real path /app/software/vendors and wrapped it in the Software shell —
        while the sidebar linked to /app/vendors. That route did not exist, so
        the link fell through and landed on the dashboard.
      */
      {
        path: "vendors",
        element: <Vendors/>,
      },
      //settings
      {
        path: "settings",
        element: <Settings />,
      },
      {
        path: "settings/holidays",
        element: <HolidaySettings />,
      },
      {
        path: "settings/geofence",
        element: <Geofence/>
      },
      {
        path: "settings/grades",
        element: <GradesBands/>
      },
      {
        path: "settings/payroll",
        element: <PayrollLayout/>,
        children: [
          { index: true, element: <PayrollOverview/> },
          { path: "dashboard", element: <PayrollOverview/> },
          { path: "runs", element: <PayrollRunDashboard/> },
          { path: "structures", element: <SalaryStructureList/> },
          { path: "components", element: <SalaryComponents/> },
          { path: "statutory", element: <StatutoryComponents/> },
          { path: "reports", element: <StatutoryReport/> },
          { path: "payslips", element: <AllPayslips/> },
          { path: "queries", element: <PayslipQueries/> },
        ],
      },
      {
        path: "settings/payroll/run/off-cycle/new",
        element: <OffCycleRun/>,
      },
      {
        path: "settings/payroll/run/:id",
        element: <PayrollRunDetail/>,
      },
      {
        path: "settings/payroll/structures/:employeeId",
        element: <SalaryStructureForm/>,
      },
      {
        path: "settings/payroll/components/new",
        element: <SalaryComponentForm/>,
      },
      {
        path: "settings/payroll/templates",
        element: <SalaryTemplates/>,
      },
      {
        path: "settings/payroll/payslip/:id",
        element: <PayslipDetail basePath="/app/settings/payroll/payslips"/>,
      },
      //performance
      {
        path: "performance",
        element: <Performance/>,
      },
      {
        path: "performance/questions",
        element: <ReviewQuestions/>,
      },
      {
        path: "performance/cycle/:id",
        element: <CycleDetail/>,
      },
      {
        path: "performance/review/:id",
        element: <ReviewDetail/>,
      },
      //timesheet
      {
        path: "timesheet",
        element: <Timesheet/>,
      },
      {
        path: "timesheet/report",
        element: <ProjectReport/>,
      },
      {
        path: "timesheet/new",
        element: <TimesheetForm/>,
      },
      {
        path: "timesheet/:id",
        element: <TimesheetDetail/>,
      },
      {
        path: "timesheet-settings",
        element: <TimeSheetGeneralSettings/>,
        children:[
          {
            index:true,
            element:<Timelog/>
          },
          {
            path:"employee-rate",
            element:<EmployeeRate/>
          },
          {
            path:"project-configuration",
            element:<ProjectConfiguration/>
          },
          {
            path:"client",
            element:<Client/>
          }
        ]
      },
      // employee side

      {
        path: "leave-request",
        element: <LeaveRequest />,
      },
      {
        path: "employee-expense",
        element: <Expense />,
      },
      {
        path: "employee-expense/add-new",
        element: <AddNewExpense/>,
      },
      {
        path: "employee-expense/my-expense",
        element: <MyExpenses/>,
      },
      {
        path: "my-team",
        element: <TeamApprovals/>,
      },
      {
        path: "my-onboarding",
        element: <MyOnboarding/>,
      },
      {
        path: "my-goals",
        element: <MyGoals/>,
      },
      {
        /* The person who fills the timesheet in. `getSelfTimesheets` has
           existed since timesheets did and was called from nowhere. */
        path: "my-timesheets",
        element: <MyTimesheets/>,
      },
      {
        path: "employee-payslips",
        element: <EmployeePayslips/>,
      },
      {
        /*
          The employee reads a document; the admin reads a statutory sheet.
          Same data, different question — so the person who was paid gets net
          pay first, and the full breakdown stays one click away.
        */
        path: "employee-payslips/:id",
        element: <PayslipDocument/>,
      },
      {
        path: "employee-payslips/:id/full",
        element: <PayslipDetail basePath="/app/employee-payslips" allowQuery/>,
      },
      {
        path: "employee-attendance-dashboard",
        element: <EmployeeAttendanceDashboard/>
      },
      {
        path: "complete-profile",
        element: <CompleteProfile/>
      }
    ],
  },
  {
    path: "/forget-password",
    element: <ForgetPassword />,
  },
  {
    path: "/signup",
    element: <Signup />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/activate",
    element: <Activate />,
  },
  {
    path: "/recover-account",
    element: <RecoverAccount />,
  },
]);

function App() {
  return (
    /* ThemeProvider outside the boundary, not inside it.
       The boundary's fallback is built from MUI components, so when it sat
       outside the theme a crash was painted in MUI's default blue — the one
       screen in the product that never looked like the product, shown at
       exactly the moment someone is trying to work out what went wrong. */
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <NotificationProvider>
          <RouterProvider router={appRouter} />
        </NotificationProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
